/* BKRTimelinePage — kleurcodering-grid per groep per tijdsblok met inline override.
 * Backend: GET /api/bkr/dag + POST /api/bkr/override.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Activity, AlertTriangle, X, Loader2 } from "lucide-react";

const STATUS_CLR = {
  conform: "bg-emerald-100 border-emerald-300 text-emerald-800",
  meer_dan_vereist: "bg-violet-100 border-violet-300 text-violet-800",
  waarschuwing: "bg-amber-100 border-amber-300 text-amber-800",
  niet_conform: "bg-red-100 border-red-300 text-red-800",
};

export default function BKRTimelinePage() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overrideCell, setOverrideCell] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bkr/dag?date=${date}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  // Verzamel alle groepen voor de dropdown
  const allGroups = [];
  (data?.locations || []).forEach(loc => {
    loc.groups.forEach(g => allGroups.push({ id: g.group_id, name: g.name, location: loc.name }));
  });

  // Gefilterde weergave
  const displayData = !data ? null : {
    ...data,
    locations: data.locations.map(loc => ({
      ...loc,
      groups: groupFilter === "all" ? loc.groups : loc.groups.filter(g => g.group_id === groupFilter),
    })).filter(loc => loc.groups.length > 0),
  };

  return (
    <div className="min-h-screen bg-neutral-50 p-4 md:p-6" data-testid="bkr-timeline-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-display flex items-center gap-2">
              <Activity className="h-6 w-6 text-emerald-600" /> BKR-timeline
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Dynamische bezettingscontrole per groep per 30-min blok. Wet IKK 50%-regel + 3-uursgrens.
            </p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-xs">Datum</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                     className="w-40" data-testid="bkr-date" />
            </div>
            {allGroups.length > 1 && (
              <div>
                <Label className="text-xs">Groep</Label>
                <select
                  value={groupFilter}
                  onChange={e => setGroupFilter(e.target.value)}
                  className="h-10 border border-neutral-300 rounded px-2 text-sm bg-white min-w-[180px]"
                  data-testid="bkr-group-filter"
                >
                  <option value="all">Alle groepen ({allGroups.length})</option>
                  {allGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.name} · {g.location}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <Card className="p-3 mb-4 bg-white text-xs flex flex-wrap gap-3">
          <span className="font-semibold">Legenda:</span>
          {[
            ["conform", "Conform"], ["meer_dan_vereist", "Meer dan vereist"],
            ["waarschuwing", "Waarschuwing (≥50%)"], ["niet_conform", "Niet conform (<50%)"],
          ].map(([k, l]) => (
            <span key={k} className={`px-2 py-0.5 border rounded ${STATUS_CLR[k]}`}>{l}</span>
          ))}
        </Card>

        {loading && <div className="text-center py-8 text-sm text-neutral-500">Laden…</div>}
        {displayData && displayData.locations.map(loc => (
          <div key={loc.location_id} className="mb-6" data-testid={`bkr-loc-${loc.location_id}`}>
            <h2 className="text-lg font-semibold mb-2">{loc.name}</h2>
            {loc.groups.length === 0 && (
              <Card className="p-3 bg-white text-sm text-neutral-500">Geen groepen</Card>
            )}
            {loc.groups.map(g => (
              <Card key={g.group_id} className="p-3 mb-2 bg-white" data-testid={`bkr-grp-${g.group_id}`}>
                <div className="flex flex-wrap items-center gap-3 mb-2 text-sm">
                  <span className="font-semibold text-neutral-900">{g.name}</span>
                  <span className="text-xs text-neutral-600">
                    {g.kinderen} kinderen · capacity {g.capacity} · PMs vereist {g.pms_vereist_dag}
                  </span>
                  {g.afwijk_totaal_min > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded ${g.afwijk_over_3u ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Afwijking: {g.afwijk_totaal_min} min {g.afwijk_over_3u && "(> 3 uur!)"}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-6 md:grid-cols-11 lg:grid-cols-22 gap-1"
                     style={{ gridTemplateColumns: `repeat(${g.timeline.length}, minmax(0, 1fr))` }}>
                  {g.timeline.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => setOverrideCell({
                        group_id: g.group_id, name: g.name,
                        van: t.van, tot: t.tot, date,
                      })}
                      className={`px-1 py-1.5 text-[9px] border rounded ${STATUS_CLR[t.status]} hover:brightness-95 cursor-pointer transition text-center`}
                      title={`${t.van}-${t.tot}: ${t.pms_aanwezig}/${t.pms_vereist} PM, ${t.kinderen} kids (${t.status})`}
                      data-testid={`bkr-cell-${g.group_id}-${i}`}
                    >
                      <div className="font-mono">{t.van}</div>
                      <div>{t.pms_aanwezig}/{t.pms_vereist}</div>
                    </button>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        ))}

        {displayData && displayData.locations.length === 0 && (
          <Card className="p-8 text-center bg-white text-sm text-neutral-500">
            {groupFilter === "all" ? "Geen locaties gevonden." : "Deze groep bestaat niet op de gekozen datum."}
          </Card>
        )}

        {overrideCell && (
          <OverrideModal cell={overrideCell} onClose={() => setOverrideCell(null)} onDone={load} />
        )}
      </div>
    </div>
  );
}

function OverrideModal({ cell, onClose, onDone }) {
  const [reden, setReden] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (reden.trim().length < 5) { toast.error("Reden minimaal 5 tekens"); return; }
    setBusy(true);
    try {
      await api.post("/bkr/override", {
        date: cell.date, group_id: cell.group_id,
        van: cell.van, tot: cell.tot, reden: reden.trim(),
      });
      toast.success("Override geregistreerd — audit-gelogd");
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}
           data-testid="bkr-override-modal">
        <div className="p-5 border-b flex items-center justify-between">
          <div className="text-lg font-semibold">BKR-override registreren</div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
            <div className="font-semibold text-amber-900">Groep: {cell.name}</div>
            <div className="text-amber-800">Periode: {cell.date} {cell.van} – {cell.tot}</div>
          </div>
          <div>
            <Label>Reden (min. 5 tekens) *</Label>
            <Textarea rows={3} value={reden} onChange={e => setReden(e.target.value)}
                      placeholder="Waarom wijkt de bezetting af? Bijv. pauzewissel, onderbouwd verzoek..."
                      data-testid="bkr-override-reden" autoFocus />
          </div>
          <p className="text-xs text-neutral-500">
            De override wordt gelogd in de auditlog met jouw account, tijdstip en groepsinfo.
          </p>
        </div>
        <div className="p-5 border-t flex justify-between">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={save} disabled={busy || reden.trim().length < 5}
                  className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bkr-override-save">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {busy ? "Opslaan…" : "Registreren"}
          </Button>
        </div>
      </div>
    </div>
  );
}
